import React from 'react';
import {
  Box,
  List,
  ListItem,
  TextField,
  IconButton,
  FormControlLabel,
  Radio,
  Typography,
  Button
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

export interface ProblemOption {
  id: string | number;
  optionText: string;
  isCorrect: boolean;
  optionOrder: number;
}

interface MultipleChoiceOptionsListProps {
  options: ProblemOption[];
  onChange: (options: ProblemOption[]) => void;
  error?: string;
}

const MultipleChoiceOptionsList: React.FC<MultipleChoiceOptionsListProps> = ({
  options,
  onChange,
  error
}) => {
  // Add a new option
  const handleAddOption = () => {
    const newOption: ProblemOption = {
      id: `temp-${Date.now()}`,
      optionText: '',
      isCorrect: options.length === 0, // First option is correct by default
      optionOrder: options.length + 1
    };
    onChange([...options, newOption]);
  };

  // Update an option
  const handleOptionChange = (index: number, field: keyof ProblemOption, value: string | boolean | number) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    
    // If marking this option as correct, unmark others
    if (field === 'isCorrect' && value === true) {
      newOptions.forEach((option, i) => {
        if (i !== index) {
          newOptions[i] = { ...newOptions[i], isCorrect: false };
        }
      });
    }
    
    onChange(newOptions);
  };

  // Delete an option
  const handleDeleteOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index);
    
    // Update order values
    const reorderedOptions = newOptions.map((opt, idx) => ({
      ...opt,
      optionOrder: idx + 1
    }));
    
    // If we deleted the correct option and there are still options left,
    // make the first one correct
    if (options[index].isCorrect && reorderedOptions.length > 0) {
      reorderedOptions[0] = { ...reorderedOptions[0], isCorrect: true };
    }
    
    onChange(reorderedOptions);
  };

  // Handle drag and drop reordering
  const handleDragEnd = (result: { destination?: { index: number } | null; source: { index: number } }) => {
    if (!result.destination) return;
    
    const items = Array.from(options);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update order values
    const reorderedOptions = items.map((item, index) => ({
      ...item,
      optionOrder: index + 1
    }));
    
    onChange(reorderedOptions);
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Multiple Choice Options
      </Typography>
      
      {error && (
        <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
          {error}
        </Typography>
      )}
      
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="options">
          {(provided) => (
            <List
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{ width: '100%', mb: 2 }}
            >
              {options.map((option, index) => (
                <Draggable key={option.id.toString()} draggableId={option.id.toString()} index={index}>
                  {(provided) => (
                    <ListItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{ 
                        border: '1px solid #eee', 
                        borderRadius: '4px', 
                        mb: 1,
                        bgcolor: option.isCorrect ? 'rgba(76, 175, 80, 0.1)' : 'transparent'
                      }}
                    >
                      <Box
                        {...provided.dragHandleProps}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mr: 1,
                          cursor: 'grab' 
                        }}
                      >
                        <DragIcon />
                      </Box>
                      
                      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ minWidth: '30px', color: 'text.secondary' }}>
                          {index + 1}.
                        </Typography>
                        
                        <TextField
                          fullWidth
                          value={option.optionText}
                          onChange={(e) => handleOptionChange(index, 'optionText', e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          size="small"
                          sx={{ mr: 2 }}
                        />
                        
                        <FormControlLabel
                          control={
                            <Radio
                              checked={option.isCorrect}
                              onChange={() => handleOptionChange(index, 'isCorrect', true)}
                              color="success"
                            />
                          }
                          label="Correct"
                        />
                        
                        <IconButton
                          onClick={() => handleDeleteOption(index)}
                          disabled={options.length <= 2}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
      
      <Button
        startIcon={<AddIcon />}
        onClick={handleAddOption}
        variant="outlined"
        size="small"
      >
        Add Option
      </Button>
    </Box>
  );
};

export default MultipleChoiceOptionsList;